import React, { useState, useMemo, useCallback } from 'react';
import { usePartners } from '../context/PartnersContext';
import { useApiMode } from '../context/ApiModeContext';
import './ApiExplorer.css';

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const GET_ONLY_SLUGS = ['countries', 'exchange-rate', 'purpose-codes', 'payment-methods'];

const getConfig = (slug, baseUrl, partners, getCustomersPartnerId, getDocId) => {
  const base = baseUrl.replace(/\/$/, '');
  const partnerId = getCustomersPartnerId || (partners[0] && partners[0].id);

  if (GET_ONLY_SLUGS.includes(slug)) {
    const pathMap = {
      'countries': '/api/v1/master-data/countries',
      'exchange-rate': '/api/v1/master-data/exchange-rate',
      'purpose-codes': '/api/v1/master-data/purpose-codes',
      'payment-methods': '/api/v1/master-data/payment-methods',
    };
    const fullUrl = `${base}${pathMap[slug]}`;
    return {
      tabs: [{ id: 'get', label: 'Get' }],
      get: {
        method: 'GET',
        fullUrl,
        response: slug === 'countries' ? { status: 'success', data: [{ code: 'US', name: 'United States' }, { code: 'IN', name: 'India' }] }
          : slug === 'exchange-rate' ? { status: 'success', data: [{ from: 'USD', to: 'INR', rate: 83.5, updated_at: '2026-02-10' }] }
          : slug === 'purpose-codes' ? { status: 'success', data: [{ code: 'P001', description: 'Family support' }, { code: 'P002', description: 'Education' }] }
          : { status: 'success', data: [{ code: 'BANK', name: 'Bank Transfer' }, { code: 'CASH', name: 'Cash Pickup' }] },
        errors: [
          { code: '401', description: 'Unauthorized – Invalid or missing API key' },
          { code: '500', description: 'Internal Server Error' },
        ],
      },
    };
  }

  if (slug === 'customer-documents') {
    const docId = getDocId || 'doc_001';
    return {
      tabs: [
        { id: 'create', label: 'Create' },
        { id: 'update', label: 'Update' },
        { id: 'get', label: 'Get' },
      ],
      create: {
        method: 'POST',
        fullUrl: `${base}/api/v1/customer-documents`,
        response: { status: 'success', code: '200', message: 'Document created', data: { id: 'doc_001', customer_id: 'CZE000001', document_type: 'passport' }, request_id: 'req_1' },
        errors: [
          { code: '400', description: 'Bad Request – Invalid or missing fields' },
          { code: '401', description: 'Unauthorized' },
          { code: '404', description: 'Customer not found' },
          { code: '500', description: 'Internal Server Error' },
        ],
      },
      update: {
        method: 'PUT',
        fullUrl: `${base}/api/v1/customer-documents/${docId}`,
        response: { status: 'success', code: '200', message: 'Document updated', data: { id: docId }, request_id: 'req_1' },
        errors: [
          { code: '400', description: 'Bad Request' },
          { code: '401', description: 'Unauthorized' },
          { code: '404', description: 'Document not found' },
          { code: '500', description: 'Internal Server Error' },
        ],
      },
      get: {
        method: 'GET',
        fullUrl: `${base}/api/v1/customer-documents?partner_id=${partnerId}`,
        response: { status: 'success', data: [{ id: 'doc_001', customer_id: 'CZE000001', document_type: 'passport', status: 'verified' }], request_id: 'req_1' },
        errors: [
          { code: '401', description: 'Unauthorized' },
          { code: '403', description: 'Forbidden' },
          { code: '500', description: 'Internal Server Error' },
        ],
      },
    };
  }

  return { tabs: [{ id: 'get', label: 'Get' }], get: { method: 'GET', fullUrl: base, response: {}, errors: [] } };
};

const MasterDataApiExplorer = ({ slug, baseUrl: baseUrlProp, onClose }) => {
  const { apiBaseUrl: contextBaseUrl } = useApiMode();
  const baseUrl = baseUrlProp || contextBaseUrl;
  const { partners, addLogForPartner } = usePartners();
  const [activeTab, setActiveTab] = useState('get');
  const [getPartnerId, setGetPartnerId] = useState(partners[0] ? String(partners[0].id) : '');
  const [docId, setDocId] = useState('doc_001');
  const [createJson, setCreateJson] = useState(() =>
    JSON.stringify({ partner_id: partners[0]?.id ?? '', customer_id: 'CZE000001', document_type: 'passport', document_url: 'https://example.com/doc.pdf' }, null, 2)
  );
  const [updateJson, setUpdateJson] = useState(() =>
    JSON.stringify({ id: 'doc_001', customer_id: 'CZE000001', document_type: 'passport', status: 'verified' }, null, 2)
  );
  const [responseText, setResponseText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const config = useMemo(
    () => getConfig(slug, baseUrl, partners, getPartnerId, docId),
    [slug, baseUrl, partners, getPartnerId, docId]
  );

  const currentTabData = config[activeTab] || config.get;
  const currentFullUrl = currentTabData?.fullUrl || '';

  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text);
  }, []);

  const handleSend = async () => {
    setError('');
    setIsLoading(true);
    const partnerName = partners.find((p) => String(p.id) === String(getPartnerId))?.name || partners[0]?.name || 'Connection';
    try {
      const url = currentTabData?.fullUrl;
      const method = currentTabData?.method || 'GET';
      const body = method !== 'GET' && (slug === 'customer-documents' && activeTab === 'create' ? createJson : slug === 'customer-documents' && activeTab === 'update' ? updateJson : undefined);
      const opts = method === 'GET' ? {} : { method, headers: { 'Content-Type': 'application/json' }, body };
      const r = await fetch(url, opts);
      const res = await r.json().catch(() => ({}));
      const responseStr = JSON.stringify(res, null, 2);
      setResponseText(responseStr);
      if (addLogForPartner && partnerName) {
        addLogForPartner(partnerName, {
          action: `Master Data API (${slug})`,
          status: r.ok ? 'Success' : 'Failed',
          details: `${activeTab}: ${responseStr.slice(0, 60)}...`,
          amount: 0,
        });
      }
    } catch (e) {
      setError(e.message || 'Request failed');
      setResponseText('');
      if (addLogForPartner && partnerName) {
        addLogForPartner(partnerName, { action: `Master Data API (${slug})`, status: 'Failed', details: String(e.message), amount: 0 });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    if (slug === 'customer-documents') {
      setCreateJson(JSON.stringify({ partner_id: partners[0]?.id ?? '', customer_id: 'CZE000001', document_type: 'passport', document_url: '' }, null, 2));
      setUpdateJson(JSON.stringify({ id: 'doc_001', customer_id: 'CZE000001', document_type: 'passport', status: 'verified' }, null, 2));
    }
    setResponseText('');
    setError('');
  };

  const errSection = currentTabData?.errors || [];

  return (
    <div className="api-explorer api-explorer-sidebar">
      <div className="api-explorer-header">
        <h3>API Explorer</h3>
        <button type="button" className="api-explorer-close" onClick={onClose} aria-label="Close">×</button>
      </div>

      <div className="api-explorer-tabs">
        {config.tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`api-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="api-explorer-panel">
        <div className="api-endpoint-block">
          <label className="api-endpoint-label">Base API endpoint</label>
          <div className="api-endpoint-value">{baseUrl}</div>
          <label className="api-endpoint-label">API endpoint</label>
          <div className="api-endpoint-row">
            <input type="text" className="api-endpoint-input" value={currentFullUrl} readOnly />
            <button type="button" className="api-endpoint-copy" onClick={() => copyToClipboard(currentFullUrl)} title="Copy endpoint" aria-label="Copy endpoint">
              <CopyIcon />
            </button>
          </div>
          <div className="api-method-badge-inline">
            <span className="api-method-badge">{currentTabData?.method || 'GET'}</span>
          </div>
        </div>

        <section className="api-doc-section">
          <h4>Request</h4>
          {slug === 'customer-documents' && activeTab === 'create' && (
            <textarea className="api-textarea" value={createJson} onChange={(e) => setCreateJson(e.target.value)} rows={8} />
          )}
          {slug === 'customer-documents' && activeTab === 'update' && (
            <>
              <div className="form-group">
                <label>Document ID (path)</label>
                <input type="text" className="form-input" value={docId} onChange={(e) => setDocId(e.target.value)} placeholder="doc_001" />
              </div>
              <textarea className="api-textarea" value={updateJson} onChange={(e) => setUpdateJson(e.target.value)} rows={6} />
            </>
          )}
          {slug === 'customer-documents' && activeTab === 'get' && (
            <div className="form-group">
              <label>partner_id (query)</label>
              <select className="form-input" value={getPartnerId} onChange={(e) => setGetPartnerId(e.target.value)}>
                <option value="">Select connection</option>
                {partners.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}
          {GET_ONLY_SLUGS.includes(slug) && (
            <pre className="api-pre">No request body. GET request with optional query parameters as per API docs.</pre>
          )}
          <div className="api-tab-actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={handleSend} disabled={isLoading}>
              {isLoading ? 'Sending...' : 'Send'}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={handleClear}>Clear</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
          </div>
        </section>

        <section className="api-doc-section">
          <h4>Response</h4>
          <pre className="api-pre api-response-pre">{JSON.stringify(currentTabData?.response || {}, null, 2)}</pre>
        </section>

        <section className="api-doc-section">
          <h4>Error</h4>
          <table className="doc-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {errSection.map((row) => (
                <tr key={row.code}>
                  <td><code>{row.code}</code></td>
                  <td>{row.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      <div className="api-response-section">
        <h4>Live response</h4>
        {error && <div className="error-message">{error}</div>}
        <textarea
          className="api-textarea response-textarea"
          readOnly
          value={responseText || (isLoading ? 'Loading...' : 'Click Send to see response')}
          rows={6}
        />
      </div>
    </div>
  );
};

export default MasterDataApiExplorer;
